import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, Plus, Archive, GitBranch, PencilLine, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AuthImage } from "@/components/admin/AuthImage";

const card = "rounded-2xl bg-white border border-slate-200 p-5";

const STATUS_STYLES = {
  draft: "bg-amber-100 text-amber-900 border border-amber-300",
  ready: "bg-emerald-100 text-emerald-900 border border-emerald-300",
  archived: "bg-slate-200 text-slate-600 border border-slate-300",
};

const fmtD = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";

function StatusChip({ status, tid }) {
  return (
    <span data-testid={tid} className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
      {status}
    </span>
  );
}

export default function PostcardStudio() {
  const [templates, setTemplates] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/admin/postcard-studio/templates")
      .then(({ data }) => setTemplates(data.templates))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load templates"));
  }, []);
  useEffect(load, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/postcard-studio/templates", {
        name: name.trim(), template_code: code.trim() || null,
      });
      toast.success("Template created");
      navigate(`/admin/postcard-studio/${data.current_version_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const act = async (label, fn) => {
    setBusy(true);
    try { await fn(); toast.success(label); load(); }
    catch (err) { toast.error(err.response?.data?.detail || `${label} failed`); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="postcard-studio-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin/property-campaigns" data-testid="ps-back-link"
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
            <ArrowLeft className="w-3.5 h-3.5" /> Property Campaigns
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 mt-1">Postcard Studio</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Import finished artwork, overlay merge fields and the AWARE property QR,
            proof real recipients, then version and assign to a campaign.
          </p>
        </div>
      </div>

      <form onSubmit={create} className={`${card} mt-5 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end`} data-testid="ps-create-form">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Template name</span>
          <input data-testid="ps-create-name" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="PWS Doonan Postcard"
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Template code (optional)</span>
          <input data-testid="ps-create-code" value={code} onChange={(e) => setCode(e.target.value)}
            placeholder="PWS-DOONAN-POSTCARD"
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-mono" />
        </label>
        <Button type="submit" data-testid="ps-create-btn" disabled={busy || !name.trim()} className="rounded-full bg-cyan-700 hover:bg-cyan-800">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Create Template
        </Button>
      </form>

      {templates === null && <div className="mt-6 text-sm text-slate-500" data-testid="ps-loading">Loading templates…</div>}
      {templates?.length === 0 && (
        <div className={`${card} mt-6 text-sm text-slate-500`} data-testid="ps-empty">
          No postcard templates yet. Create one above, then upload your finished front and reverse artwork.
        </div>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {(templates || []).map((t) => {
          const cur = t.current;
          return (
            <div key={t.id} className={card} data-testid={`ps-template-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 truncate" data-testid={`ps-template-name-${t.id}`}>{t.name}</div>
                  <div className="text-[11px] font-mono text-slate-500">{t.template_code}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusChip status={t.status} tid={`ps-template-status-${t.id}`} />
                  <span className="text-[11px] font-bold text-slate-600" data-testid={`ps-template-version-${t.id}`}>V{t.current_version}</span>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Front</div>
                  <AuthImage testId={`ps-thumb-front-${t.id}`} subtle
                    path={cur?.id ? `/admin/postcard-studio/versions/${cur.id}/artwork/front.png` : null}
                    alt="Front artwork" className="w-full h-24 object-contain rounded-lg border border-slate-200 bg-slate-50" />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Reverse</div>
                  <AuthImage testId={`ps-thumb-back-${t.id}`} subtle
                    path={cur?.id ? `/admin/postcard-studio/versions/${cur.id}/artwork/back.png` : null}
                    alt="Reverse artwork" className="w-full h-24 object-contain rounded-lg border border-slate-200 bg-slate-50" />
                </div>
              </div>

              <div className="mt-3 text-[11px] text-slate-500">
                Modified {fmtD(t.updated_at)} · {t.versions.length} version{t.versions.length === 1 ? "" : "s"}
                {cur?.locked_at && <span className="ml-1 text-rose-700 font-semibold">· V{cur.version_number} locked (mailed)</span>}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-full" data-testid={`ps-open-${t.id}`}
                  onClick={() => navigate(`/admin/postcard-studio/${cur?.id}`)} disabled={!cur?.id}>
                  <PencilLine className="w-3.5 h-3.5 mr-1" /> Open
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" data-testid={`ps-duplicate-${t.id}`} disabled={busy}
                  onClick={() => act("Template duplicated", () => api.post(`/admin/postcard-studio/templates/${t.id}/duplicate`, {}))}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Duplicate
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" data-testid={`ps-new-version-${t.id}`} disabled={busy}
                  onClick={() => act("New version created", async () => {
                    const { data } = await api.post(`/admin/postcard-studio/templates/${t.id}/versions`);
                    navigate(`/admin/postcard-studio/${data.id}`);
                  })}>
                  <GitBranch className="w-3.5 h-3.5 mr-1" /> Create New Version
                </Button>
                {t.status !== "archived" && (
                  <Button size="sm" variant="outline" className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`ps-archive-${t.id}`} disabled={busy}
                    onClick={() => window.confirm(`Archive "${t.name}"? It can no longer be assigned to a campaign.`)
                      && act("Template archived", () => api.post(`/admin/postcard-studio/templates/${t.id}/archive`))}>
                    <Archive className="w-3.5 h-3.5 mr-1" /> Archive
                  </Button>
                )}
              </div>

              {t.versions.length > 1 && (
                <div className="mt-3 border-t border-slate-100 pt-2 space-y-1" data-testid={`ps-versions-${t.id}`}>
                  {t.versions.map((v) => (
                    <div key={v.id} className="flex items-center gap-2 text-[11px]">
                      <Link to={`/admin/postcard-studio/${v.id}`} className="font-semibold text-cyan-700 hover:text-cyan-900" data-testid={`ps-version-link-${v.id}`}>
                        V{v.version_number}
                      </Link>
                      <StatusChip status={v.status} tid={`ps-version-status-${v.id}`} />
                      {v.preflight && <span className={v.preflight === "PASS" ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>{v.preflight}</span>}
                      {v.locked_at && <span className="text-rose-700 font-semibold">locked</span>}
                      <span className="text-slate-400 ml-auto">{fmtD(v.updated_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
